/**
 * The run-through page's share of the contract (handoff 4), the same for both order simulators:
 * each stage lands as rows built by the engine from its record, a change says what it re-runs, and
 * a stop is the one question the draft cannot be written without. The OIO and OIA contracts
 * re-export these with their own stage names.
 */
import { mergeSources, type SourceRef, type SourcesPayload } from "./sources"

export type RowKind = "computed" | "read" | "chosen"
export type RowAction = "change" | "change_call" | "restore" | "add_document"
/** One line of a landed stage, built by the engine from the stage's record. */
export interface Row {
  id: string
  kind: RowKind
  label?: string
  text: string
  sub?: string
  working?: string
  /** A read row's call: "confirm" | "reduce" | "drop" on an allegation; "accepted" | "partly_accepted" | "rejected" on a ground. */
  call?: string
  amount?: number
  /** OIO quantum rows: the penalty beside the tax. */
  penalty?: number
  /** A small table under the row; the first line is the header. */
  table?: string[][]
  /** OIA relief rows: what the order sustains beside what it sets aside. */
  sustained?: number
  actions: RowAction[]
}
/** A question the engine answered for itself: "Nira chose". */
export interface DefaultChoice { row_id: string; rule: string; text: string }
export interface StageLandedPayload<S extends string = string> { stage: S; rows: Row[]; chosen: DefaultChoice[]; duration_ms?: number }
export interface RowChangedPayload<S extends string = string> { stage: S; row_id: string; by: "user" | "nira"; from: string; to: string; why: string; reruns: S[] }
export interface RunRerunPayload<S extends string = string> { from_stage: S; stages: S[]; sections: string[]; estimate_ms: number }
export type StopKind = "no_scn" | "no_order" | "required_fact" | "double_count" | "officer_call" | "document"
export interface StopPayload<S extends string = string> {
  kind: StopKind; question: string; why: string; field?: string; options: string[]; default?: string; continues: S[]
}

export type LandedStage<S extends string = string> = StageLandedPayload<S> & { stale?: boolean }

/** The run-through fields of a thread view. */
export interface RunThroughState<S extends string = string> {
  /** Each landed stage's rows, as last landed. `stale` while a change re-runs it. */
  stages: Partial<Record<S, LandedStage<S>>>
  /** Rows the person changed, latest last (the original call is kept for "restore"). */
  changes: RowChangedPayload<S>[]
  /** What a change is re-running, until the run ends. */
  rerun: RunRerunPayload<S> | null
  /** A stop waiting for an answer: the card at the top of the page. */
  stop: StopPayload<S> | null
  /** The legal sources each stage's turns relied on, kept per stage in the order first seen. */
  sources: Partial<Record<S, SourceRef[]>>
  mode: string
}

export const emptyRunThrough = <S extends string>(): RunThroughState<S> => ({ stages: {}, changes: [], rerun: null, stop: null, sources: {}, mode: "" })

export type RunThroughFrame<S extends string> =
  | { type: "stage.landed"; payload: StageLandedPayload<S> }
  | { type: "row.changed"; payload: RowChangedPayload<S> }
  | { type: "run.rerun"; payload: RunRerunPayload<S> }
  | { type: "stop"; payload: StopPayload<S> }
  | { type: "sources"; payload: SourcesPayload<S> }

interface Pending { pendingQuestion: { kind: string; [key: string]: unknown } | null }

/** One run-through event into a thread view; any other event is not this reducer's. */
export function applyRunThrough<S extends string, V extends RunThroughState<S> & Pending>(view: V, frame: RunThroughFrame<S>): V {
  switch (frame.type) {
    case "stage.landed":
      return { ...view, stages: { ...view.stages, [frame.payload.stage]: { ...frame.payload, stale: false } } }
    case "row.changed": {
      // What reads the changed row dims until it lands again; the rest stays readable.
      const stages: Partial<Record<S, LandedStage<S>>> = { ...view.stages }
      for (const st of [frame.payload.stage, ...frame.payload.reruns]) {
        const cur = stages[st]
        if (cur) stages[st] = { ...cur, stale: true }
      }
      return { ...view, stages, changes: [...view.changes, frame.payload] }
    }
    case "run.rerun":
      return { ...view, rerun: frame.payload }
    case "stop":
      return { ...view, stop: frame.payload, pendingQuestion: { ...frame.payload, kind: "stop", stop_kind: frame.payload.kind } }
    case "sources": {
      const { stage, sources } = frame.payload
      return { ...view, sources: { ...view.sources, [stage]: mergeSources(view.sources[stage], sources) } }
    }
  }
}

/** A GET /threads/{id} snapshot's run-through fields (only those: the caller spreads them over its view). */
export function runThroughSnapshot<S extends string>(
  view: RunThroughState<S>,
  snap: { stages?: Partial<Record<S, LandedStage<S>>>; pending_question: { kind: string } | null; mode: string },
): Pick<RunThroughState<S>, "stages" | "stop" | "mode"> {
  return {
    stages: snap.stages ? { ...snap.stages } : view.stages,
    stop: snap.pending_question?.kind === "stop" ? (snap.pending_question as unknown as StopPayload<S>) : view.stop,
    mode: snap.mode,
  }
}
